import { Component, OnInit, Input, ComponentRef, OnDestroy } from '@angular/core';
import { SettingsService } from '../../../services/settings.service';
import { ProgramIncrementService } from '../../../services/programIncrement.service';
import { ProgramIncrement } from '../../../models/programIncrement';
import { Program } from '../../../models/program';
import { CreateProgramIncrementDialogComponent } from '../../create-program-increment-dialog/create-program-increment-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProgramService } from '../../../services/program.service';
import { Subscription } from 'rxjs';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { CreateProgramDialogComponent } from '../../create-program-dialog/create-program-dialog.component';
import { AcknowledgeDialogComponent } from '../../dialogs/acknowledge-dialog/acknowledge-dialog.component';
import { add } from 'date-fns';

@Component({
  selector: 'app-pi-widget',
  templateUrl: './pi-widget.component.html',
  styleUrls: ['./pi-widget.component.scss']
  })
export class PiWidgetComponent implements OnInit, OnDestroy {
  static widgetName = 'programIncrement';

  availableProgramIncrements: ProgramIncrement[] = [];
  sprintPrefix = '';
  availablePrograms: Program[];
  private subscriptions: Subscription[] = [];
  public selectedProgramKey;
  public selectedPIKey;
  public programIncrementIdToDelete;
  public programIdToDeleteOrEdit;

  @Input() widget = true;

  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;
  private createProgramIncrementDialogRef: MatDialogRef<CreateProgramIncrementDialogComponent>;

  constructor(
    private programIncrementService: ProgramIncrementService,
    private programService: ProgramService,
    private settingsService: SettingsService,
    private matDialog: MatDialog,
    protected translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.loadPrograms((programs) => {
      this.availablePrograms = programs;
      this.tryRestoreLastSelectedProgram();
      if (this.selectedProgramKey) {
        this.loadProgramIncrementsForProgram(parseInt(this.selectedProgramKey, 10));
      }
    });
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  public getName(): string {
    return PiWidgetComponent.widgetName;
  }

  public setUndocked(isUndocked: boolean): void {
    this.isUndocked = isUndocked;
  }

  /**
   * Load all Projects to populate Project-Select-Options.
   * Load Sprints for current Project to populate the Sprint-Select-Options.
   */
  loadPrograms(callback: Function): void {
    this.programService.getAllPrograms().subscribe(programs => {
      callback(programs);
    });
  }

  loadProgramIncrementsForProgram(programId: number): void {
    this.programIncrementService.getProgramIncrementsByProgram(programId).subscribe({
      next: (programIncrements) => {
        this.availableProgramIncrements = programIncrements;
        this.sortAvailableIncrements();
        this.setSprintPrefix();
        this.tryRestoreLastSelectedPI(programId);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  /**
  * If there is no program selected we try set program that was selected last time
  */
  private tryRestoreLastSelectedProgram(): void {
    const lastUsedProgramKey = this.settingsService.getProgramId();
    // Set last used from localstorage if A) no program selected B) last used is in localstorage C) last used is an availably program.id
    if (!this.selectedProgramKey && lastUsedProgramKey && this.availablePrograms.find(program => program.id === lastUsedProgramKey)) {
      this.selectedProgramKey = lastUsedProgramKey;
    }
    this.settingsService.emitSettingsUpdate(SettingsEventType.PROGRAM_CHANGED, this.constructor.name);
  }

  /**
  * If there is no pi selected we try set pi that was selected last time
  */
  private tryRestoreLastSelectedPI(programId: number): void {
    const lastUsedPIKey = this.settingsService.getProgramIncrementId(programId);
    // Set last used from localstorage if A) no incr. selected B) last used is in localstorage C) last used is an availably incr.id
    if (!this.selectedPIKey && lastUsedPIKey && this.availableProgramIncrements.find(pi => pi.id === lastUsedPIKey)) {
      this.selectedPIKey = lastUsedPIKey;
    }
    this.settingsService.emitSettingsUpdate(SettingsEventType.PROGRAM_INCREMENT_CHANGED, this.constructor.name);
  }

  /**
   * Load new Project and reset selectedSprint.
   * Close team widget if project changed in dropdown.
   */
  onSelectedProgramChanged(): void {
    this.selectedPIKey = null;
    this.settingsService.setProgramId(parseInt(this.selectedProgramKey, 10));
    this.loadProgramIncrementsForProgram(parseInt(this.selectedProgramKey, 10));
  }

  /**
   * Load new ProgramIncrements
   */
  onSelectedPIChanged(): void {
    const selectedIncrement = this.availableProgramIncrements.find(increment => increment.id === parseInt(this.selectedPIKey, 10));
    if (selectedIncrement) {
      this.settingsService.setProgramIncrementId(this.selectedProgramKey, this.selectedPIKey);
      this.settingsService.emitSettingsUpdate(SettingsEventType.PROGRAM_INCREMENT_CHANGED, this.constructor.name);
    }
  }

  private sortAvailableIncrements(): void {
    this.availableProgramIncrements = this.programIncrementService.sortByDate(this.availableProgramIncrements, true);
  }

  private setSprintPrefix(): void {
    const sprintsWithPrefixes = this.availableProgramIncrements.filter(pi => pi.sprintPrefix);
    if (sprintsWithPrefixes.length > 0) {
      this.sprintPrefix = sprintsWithPrefixes[sprintsWithPrefixes.length - 1].sprintPrefix;
    }
  }

  deleteProgramIncrement(programIncrementId: number, programId: number): void {
    this.programIncrementService.delete(programIncrementId, programId).subscribe({
      next: () => {
        this.availableProgramIncrements =
          this.availableProgramIncrements.filter(pi => pi.id !== Number(this.programIncrementIdToDelete));
        this.programIncrementIdToDelete = undefined;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  /**
   * Returns the minimum date for creating a new programincrement (latest endDate + 1 day)
   */
  getMinDate(): Date {
    const NEXT_DAY_OFFSET = 1;
    this.sortAvailableIncrements();
    const dates = this.availableProgramIncrements.map(pi => pi.dateEnd);
    if (dates.length > 0) {
      const latestEndDate = dates[0];
      const newDate = add(new Date(latestEndDate), { days: NEXT_DAY_OFFSET });
      return newDate;
    }
    return undefined;
  }

  addProgramIncrement(): void {
    this.createProgramIncrementDialogRef = this.matDialog.open(CreateProgramIncrementDialogComponent, {
      data: {
        name: '',
        goal: '',
        start: null,
        numberOfSprints: undefined,
        sprintDuration: undefined,
        sprintPrefix: this.sprintPrefix,
        programId: this.selectedProgramKey,
        minDate: this.getMinDate()
      }
    });

    this.createProgramIncrementDialogRef.afterClosed().subscribe(data => {
      if (data) {
        this.programIncrementService.create(data).subscribe(res => {
          this.availableProgramIncrements = [...this.availableProgramIncrements || [], res.program_increment];
        });
      }
    });
  }

  onDelete() {
    this.sortAvailableIncrements();
    if (this.availableProgramIncrements[0].id !== parseInt(this.programIncrementIdToDelete, 10)) {
      this.openUnableToDeleteDialog();
      return;
    }
    this.openConfirmDeleteDialog();
  }

  openUnableToDeleteDialog(): void {
    const title = this.translate.instant('widgets.programIncrement.unableToDeleteDialogTitle');
    const message = this.translate.instant('widgets.programIncrement.unableToDeleteDialogText');
    const okText = this.translate.instant('widgets.programIncrement.unableToDeleteDialogOkText');

    const dialogRef = this.matDialog.open(AcknowledgeDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText }
    });
  }

  openProgramCreationDialog(): void {
    const that = this;
    const dialogRef = that.matDialog.open(CreateProgramDialogComponent, { data: { name: '', description: '' } });
    dialogRef.afterClosed().subscribe(programData => that.createProgram(programData));
  }

  createProgram(data): void {
    this.programService.create(data).subscribe({
      next: (program) => {
        this.availablePrograms.push(program);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  editProgram(data): void{
    this.programService.update(this.programIdToDeleteOrEdit, data).subscribe({
      next: (program) => {
        const prg = this.availablePrograms.filter(p => p.id === program.id)[0];
        const index = this.availablePrograms.indexOf(prg);
        this.availablePrograms[index] = program;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  onDeleteProgram(): void {
    this.programService.delete(this.programIdToDeleteOrEdit).subscribe({
      next: () => {
        this.availablePrograms = this.availablePrograms.filter(p => p.id !== Number(this.programIdToDeleteOrEdit));
        this.availableProgramIncrements = [];
        this.programIdToDeleteOrEdit = undefined;
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  onEditProgram() {
    const that = this;
    const program = that.availablePrograms.filter(p => p.id === this.programIdToDeleteOrEdit)[0];
    const dialogRef = that.matDialog.open(CreateProgramDialogComponent, { data: { name: program.name, description: program.description } });
    dialogRef.afterClosed().subscribe(programData => that.editProgram(programData));
  }

  openConfirmDeleteDialog(): void {
    const that = this;
    const title = that.translate.instant('widgets.programIncrement.deleteConfirmationTitle');
    const message = that.translate.instant('widgets.programIncrement.deleteConfirmationText');
    const okText = that.translate.instant('widgets.programIncrement.deleteConfirmationOkText');
    const cancelText = that.translate.instant('widgets.programIncrement.deleteConfirmationCancelText');

    const dialogRef = that.matDialog.open(ConfirmationDialogComponent, {
      data: { title: title, message: message, buttonTextConfirm: okText, buttonTextCancel: cancelText }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        that.deleteProgramIncrement(that.programIncrementIdToDelete, that.selectedProgramKey);
      }
    });
  }

}
